import {IconButton, Link} from '@wandb/components';
import EditableField from '@wandb/weave/common/components/EditableField';
import {useWindowSize} from '@wandb/weave/common/hooks/useWindowSize';
import {A} from '@wandb/weave/common/util/links';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
import {Icon} from '@wandb/weave/components/Icon';
import copyToClipboard from 'copy-to-clipboard';
import React, {useMemo, useState} from 'react';
import {toast} from 'react-toastify';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon as SemanticIcon, Message, Modal} from 'semantic-ui-react';

import DeleteRunModal from '../../components/DeleteRunModal';
import {LoaderProxy} from '../../containers/AppLoader';
import {useRunPageHeaderQuery} from '../../generated/graphql';
import * as GQLTypes from '../../types/graphql';
import DOC_URLS from '../../util/doc_urls';
import {safeSessionStorage} from '../../util/localStorage';
import {useStatsigGateWorkspaceConsolidation} from '../../util/rampFeatureFlags';
import * as RunHelpers from '../../util/runhelpers';
import * as urls from '../../util/urls';
import {useUpdateRun} from './useUpdateRun';

type RunPageHeaderProps = {
  readOnly: boolean;
  run?: GQLTypes.RunPageRunQuery;
  entityName: string;
  projectName: string;
  runName: string;
  jobId?: string;
  cloneRun: (jobId: string) => void;
};

const RunPageHeader = ({
  readOnly,
  run: runFromProps,
  entityName,
  projectName,
  runName,
  jobId,
  cloneRun,
}: RunPageHeaderProps) => {
  const {responsiveSize} = useWindowSize();
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const handleDropdownItemClick = (callback?: () => void) => {
    setIsDropdownOpen(false);
    callback?.();
  };
  const [isExportDataModalOpen, setIsExportDataModalOpen] = useState(false);
  const [isViewCodeModalOpen, setIsViewCodeModalOpen] = useState(false);
  const runUrl = urls.run({
    entityName,
    projectName,
    name: runName,
  });
  const createRunCodeSnippet = `import wandb
api = wandb.Api()
run = api.run("${runUrl}")`;

  const isWorkspaceConsolidationEnabled =
    useStatsigGateWorkspaceConsolidation();

  const exitUrl = useMemo(() => {
    const sourcePath = safeSessionStorage.getItem('runDetailsSourcePath');
    if (sourcePath) {
      safeSessionStorage.removeItem('runDetailsSourcePath');
      return sourcePath;
    } else {
      return urls.run({
        entityName,
        projectName,
        name: runName,
      });
    }
  }, [entityName, projectName, runName]);

  const runHeaderQuery = useRunPageHeaderQuery({
    variables: {
      entityName,
      projectName,
      runName,
    },
    skip: !isWorkspaceConsolidationEnabled,
  });
  const run = isWorkspaceConsolidationEnabled
    ? runHeaderQuery.data?.project?.run
    : runFromProps;
  const updateRun = useUpdateRun(run);

  if (isWorkspaceConsolidationEnabled && runHeaderQuery.loading) {
    return <LoaderProxy name="run-header-query-loading-loader" />;
  }

  if (!run) {
    return null;
  }

  const hasGitHub = run.commit && run.github;
  const codeIcon = hasGitHub ? 'github' : 'code-alt';
  return (
    <div className="name-header-wrapper gap-14">
      {isWorkspaceConsolidationEnabled && (
        <div className="shrink-0">
          <IconButton
            icon="close"
            variant="secondary"
            aria-label="Close run details"
            href={exitUrl}
          />
        </div>
      )}
      <div className="name-header-content gap-14">
        {isWorkspaceConsolidationEnabled && (
          <div className="flex items-center w-full gap-8">
            <EditableField
              className="name-header-consolidated"
              readOnly={readOnly}
              placeholder={runName}
              value={run.displayName ?? ''}
              save={async value => {
                const result = await updateRun({displayName: value});
                if (result.success) {
                  window.analytics?.track('Run name edited');
                } else {
                  toast.error(result.error);
                }
              }}
              spellCheck={false}
              autoSelect={false}
              setIsEditing={setIsEditing}
            />
            {isEditing && (
              <p className="rounded bg-moon-100 px-6 text-sm text-moon-500 ">
                Enter
              </p>
            )}
          </div>
        )}
        {!isWorkspaceConsolidationEnabled && (
          <EditableField
            className="name-header"
            readOnly={readOnly}
            asHeader="h1"
            placeholder={runName}
            showEditIcon
            value={run.displayName ?? ''}
            save={async value => {
              const result = await updateRun({displayName: value});
              if (result.success) {
                window.analytics?.track('Run name edited');
              } else {
                toast.error(result.error);
              }
            }}
          />
        )}
        <div className="name-header-right">
          {responsiveSize !== 'small' && (
            <Button
              data-test="clone-run-button"
              icon="rocket-launch"
              variant="secondary"
              disabled={jobId == null}
              onClick={() => cloneRun(jobId ?? '')}
              tooltip={
                jobId != null
                  ? undefined
                  : 'This run cannot be cloned because it is not associated with a Launch job.'
              }>
              Clone run with Launch
            </Button>
          )}
          <Modal
            open={isExportDataModalOpen}
            onClose={() => setIsExportDataModalOpen(false)}>
            <Modal.Header>
              <h1>Python API</h1>
            </Modal.Header>
            <Modal.Content>
              <h4>Create the run object:</h4>
              <pre className="language-python" style={{padding: '0em'}}>
                {createRunCodeSnippet}
              </pre>
              <h4>Show history data:</h4>
              <pre className="language-python">{`print(run.history())`}</pre>
              <p style={{marginTop: 20}}>
                Check out the{' '}
                <Link href={DOC_URLS.publicApi} target="_blank">
                  API docs
                </Link>
                {' for more information.'}
              </p>
            </Modal.Content>
          </Modal>
          <Modal
            open={isViewCodeModalOpen}
            onClose={() => setIsViewCodeModalOpen(false)}
            on="click">
            <Modal.Header>
              <h1>View Code</h1>
            </Modal.Header>
            <Modal.Content>
              <Message icon>
                <SemanticIcon name={hasGitHub ? 'github' : 'code'} />
                <Message.Content>
                  <p>
                    Wandb will automatically save the git commit ID of the last
                    commit before each of your runs.
                  </p>
                  {hasGitHub ? (
                    <>
                      <p>
                        You can find your commit{' '}
                        <Link href={run.github ?? ''} target="_blank">
                          here on GitHub
                        </Link>
                        .
                      </p>
                    </>
                  ) : run.runInfo && run.runInfo.git ? (
                    <table className="run-page-table">
                      <tr>
                        <td className="key">
                          <span className="overview-key">Git remote</span>
                        </td>
                        <td className="command">
                          {run.runInfo.git.remote ?? '-'}
                        </td>
                      </tr>
                      <tr>
                        <td className="key">
                          <span className="overview-key">Git commit</span>
                        </td>
                        <td className="command">
                          {run.runInfo.git.commit ?? '-'}
                        </td>
                      </tr>
                    </table>
                  ) : (
                    <p>
                      However, we couldn't find a git commit for this particular
                      run.
                    </p>
                  )}
                  {RunHelpers.hasDiffPatchFile(run.diffPatchFile) && (
                    <p>
                      Your uncommitted changes were saved in file called{' '}
                      <strong>diff.patch</strong>, which you can download{' '}
                      <A
                        href={urls.runDiffPatchFileUrl({
                          entityName,
                          projectName,
                          runName,
                        })}
                        download="diff.patch">
                        here
                      </A>
                    </p>
                  )}
                </Message.Content>
              </Message>
            </Modal.Content>
          </Modal>
          <DeleteRunModal
            closeModal={() => setDeleteModalOpen(false)}
            isOpen={isDeleteModalOpen}
            setIsModalOpen={setDeleteModalOpen}
            entityName={entityName}
            isRunActive={run.state === 'running'}
            projectName={projectName}
            runDisplayName={run.displayName ?? ''}
            runId={run.id ?? ''}
            runName={run.name ?? ''}
          />
          <DropdownMenu.Root
            open={isDropdownOpen}
            onOpenChange={setIsDropdownOpen}>
            <DropdownMenu.Trigger>
              <IconButton
                isActive={isDropdownOpen}
                icon="overflow-horizontal"
                variant="secondary"
                aria-label="Open export modal"
              />
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content align="end">
                <DropdownMenu.Item
                  onClick={() => {
                    setIsExportDataModalOpen(true);
                  }}>
                  <Icon name="database-artifacts" />
                  Export Data
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  onClick={() =>
                    handleDropdownItemClick(() => setIsViewCodeModalOpen(true))
                  }>
                  <Icon name={codeIcon} />
                  View Code
                </DropdownMenu.Item>
                {run.internalID && (
                  <DropdownMenu.Item
                    onClick={() => {
                      copyToClipboard(run.internalID!);
                      handleDropdownItemClick();
                      window.analytics?.track('Immutable Run ID copied');
                    }}>
                    <Icon name="copy" />
                    Copy Immutable Run ID
                  </DropdownMenu.Item>
                )}
                {!readOnly && (
                  <>
                    <DropdownMenu.Separator />
                    <DropdownMenu.Item
                      content="Delete run"
                      onClick={() =>
                        handleDropdownItemClick(() => setDeleteModalOpen(true))
                      }>
                      <Icon name="delete" />
                      Delete run
                    </DropdownMenu.Item>
                  </>
                )}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>
      </div>
    </div>
  );
};

export default RunPageHeader;
